import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { StitchModel, StitchTypeEnum } from 'src/app/models/stitchs/stitch_model';
import { UnitOfMeasureEnum, HttpService } from 'src/app/services/http-service';
import { Utility, BeadMeasureEnum, BeadTypeEnum } from 'src/app/helpers/utility';
import { ITranslation } from 'src/app/models/translations/translations';
import { PaletteRegistryService } from 'src/app/services/palette-registry.service';

interface BeadTypeOption {
  id: BeadTypeEnum;
  paletteId: number;
  label: string;
  sizeNote: string;
}

export interface NewPatternSetupModalData {
  stitches: StitchModel[];
  unitOfMeasure: UnitOfMeasureEnum;
  trigger: 'new' | 'onboarding';
}

export interface PatternSetupResult {
  stitchId: StitchTypeEnum;
  columns: number;
  rows: number;
}

interface PatternPreset {
  key: string;
  label: string;
  stitchId: StitchTypeEnum;
  columns: number;
  rows: number;
}

@Component({
  selector: 'app-new-pattern-setup-modal',
  templateUrl: './new-pattern-setup-modal.component.html',
  styleUrls: ['./new-pattern-setup-modal.component.scss'],
})
export class NewPatternSetupModalComponent implements OnInit {
  public readonly stitches: StitchModel[];
  public readonly unitOfMeasure: UnitOfMeasureEnum;
  public selectedStitchId: StitchTypeEnum;
  public columns = 32;
  public rows = 120;
  public selectedPresetKey: string | null = null;
  public presets: PatternPreset[] = [];
  public t: ITranslation;
  public selectedBeadType: BeadTypeEnum = BeadTypeEnum.MiyukiDelica11;
  public beadTypeOptions: BeadTypeOption[] = [];

  private buildBeadTypeOptions(): BeadTypeOption[] {
    return [
      {
        id: BeadTypeEnum.MiyukiDelica11,
        paletteId: this.paletteRegistry.getMiyukiDelicaId() ?? 0,
        label: this.t.BeadTypeMiyukiDelica || 'Miyuki Delica 11/0',
        sizeNote: this.t.BeadTypeMiyukiDelicaNote || 'Dimensions based on Miyuki Delica 11/0 beads (~1.3×1.6 mm). Measurements are approximate.',
      },
      {
        id: BeadTypeEnum.PreciosaRocailles11,
        paletteId: this.paletteRegistry.getPreciosaRocaillesId() ?? 0,
        label: this.t.BeadTypePreciosaRocailles || 'Preciosa Rocailles 11/0',
        sizeNote: this.t.BeadTypePreciosaRocaillesNote || 'Dimensions based on Preciosa Rocailles 11/0 beads (~2.1 mm). Measurements are approximate.',
      },
    ];
  }

  private buildPresets(): PatternPreset[] {
    return [
      {
        key: 'peyote-bracelet',
        label: this.t.PresetClassicPeyoteBracelet,
        stitchId: StitchTypeEnum.Peyote,
        columns: 32,
        rows: 120,
      },
      {
        key: 'loom-bookmark',
        label: this.t.PresetLoomBookmark,
        stitchId: StitchTypeEnum.Loom,
        columns: 34,
        rows: 150,
      },
      {
        key: 'brick-earring',
        label: this.t.PresetBrickEarringsPair,
        stitchId: StitchTypeEnum.Brick,
        columns: 18,
        rows: 60,
      },
      {
        key: 'peyote-cuff',
        label: this.t.PresetWidePeyoteCuff,
        stitchId: StitchTypeEnum.Peyote,
        columns: 50,
        rows: 120,
      },
      {
        key: 'loom-small',
        label: this.t.PresetSmallLoomPanel,
        stitchId: StitchTypeEnum.Loom,
        columns: 20,
        rows: 80,
      },
      {
        key: 'brick-pendant',
        label: this.t.PresetBrickPendant,
        stitchId: StitchTypeEnum.Brick,
        columns: 25,
        rows: 80,
      },
    ];
  }

  constructor(
    public dialogRef: MatDialogRef<NewPatternSetupModalComponent>,
    @Inject(MAT_DIALOG_DATA) private data: NewPatternSetupModalData,
    public httpService: HttpService,
    private paletteRegistry: PaletteRegistryService
  ) {
    this.stitches = data.stitches ?? [];
    this.unitOfMeasure = data.unitOfMeasure ?? UnitOfMeasureEnum.Inch;
    this.selectedStitchId =
      this.stitches[0]?.stitchId ?? StitchTypeEnum.Peyote;

    // Cache translation object to avoid repeated getter calls
    this.t = this.httpService.getTransation();
  }

  ngOnInit(): void {
    // Build options once after translations are cached
    this.beadTypeOptions = this.buildBeadTypeOptions();
    this.presets = this.buildPresets();

    // Apply first preset
    if (this.presets.length > 0) {
      this.applyPreset(this.presets[0]);
    }
  }

  public onPresetChange(presetKey: string | null): void {
    if (!presetKey) {
      this.selectedPresetKey = null;
      return;
    }

    const preset = this.presets.find((p) => p.key === presetKey);
    if (preset) {
      this.applyPreset(preset);
    }
  }

  public onStitchChange(stitchId: StitchTypeEnum): void {
    this.selectedStitchId = stitchId;
    this.selectedPresetKey = null;
  }

  public onBeadTypeChange(beadType: BeadTypeEnum): void {
    this.selectedBeadType = beadType;
    this.selectedPresetKey = null;
  }

  public onColumnsInput(value: number | string): void {
    const numeric = typeof value === 'number' ? value : Number(value);
    this.columns = Number.isNaN(numeric) ? 5 : numeric;
    this.selectedPresetKey = null;
  }

  public onRowsInput(value: number | string): void {
    const numeric = typeof value === 'number' ? value : Number(value);
    this.rows = Number.isNaN(numeric) ? 5 : numeric;
    this.selectedPresetKey = null;
  }

  public onColumnsBlur(): void {
    this.columns = this.normalizeDimension(this.columns);
  }

  public onRowsBlur(): void {
    this.rows = this.normalizeDimension(this.rows);
  }

  public applyPreset(preset: PatternPreset): void {
    this.selectedPresetKey = preset.key;
    this.selectedStitchId = preset.stitchId;
    this.columns = preset.columns;
    this.rows = preset.rows;
  }

  public get columnSizeLabel(): string {
    return Utility.convertBeadsToSize(
      this.selectedStitchId,
      this.columns,
      this.selectedBeadType,
      BeadMeasureEnum.Width,
      this.unitOfMeasure
    );
  }

  public get rowSizeLabel(): string {
    return Utility.convertBeadsToSize(
      this.selectedStitchId,
      this.rows,
      this.selectedBeadType,
      BeadMeasureEnum.Height,
      this.unitOfMeasure
    );
  }

  public get dimensionsNote(): string {
    const option = this.beadTypeOptions.find(o => o.id === this.selectedBeadType);
    return option?.sizeNote || '';
  }

  public get physicalSizeSummary(): string {
    return `${this.columnSizeLabel} x ${this.rowSizeLabel}`;
  }

  public getStitchName(stitchId: StitchTypeEnum): string {
    return this.stitches.find((s) => s.stitchId === stitchId)?.name ?? '';
  }

  public getPresetDescription(preset: PatternPreset): string {
    const width = Utility.convertBeadsToSize(
      preset.stitchId,
      preset.columns,
      this.selectedBeadType,
      BeadMeasureEnum.Width,
      this.unitOfMeasure
    );

    const height = Utility.convertBeadsToSize(
      preset.stitchId,
      preset.rows,
      this.selectedBeadType,
      BeadMeasureEnum.Height,
      this.unitOfMeasure
    );

    return `~${width} x ${height}`;
  }

  public isValid(): boolean {
    return (
      this.columns >= 5 &&
      this.columns <= 500 &&
      this.rows >= 5 &&
      this.rows <= 500
    );
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onConfirm(): void {
    if (!this.isValid()) {
      return;
    }

    const result: PatternSetupResult = {
      stitchId: this.selectedStitchId,
      columns: Math.round(this.columns),
      rows: Math.round(this.rows),
    };

    this.dialogRef.close(result);
  }

  private normalizeDimension(value: number | string): number {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(numeric)) {
      return 5;
    }

    return Math.min(500, Math.max(5, Math.round(numeric)));
  }
}
